import { useShowError } from '@aserto/console-common'
import { MutationOptions, QueryKey, useQueryClient } from '@tanstack/react-query'

import { RpcStatus } from './tenant'

export enum MutationKeys {
  AddConfiguration,
  UpdateConfiguration,
}

export enum QueryKeys {
  Account = 'Account',
  AccountInvitesData = 'accountInvitesData',
  AonaIdentity = 'AonaIdentity',
  AuthzDataDecisiontree = 'authzDataDecisiontree',
  AuthzDataIs = 'authzDataIs',
  AuthzDataQuery = 'authzDataQuery',
  Config = 'Config',
  Configurations = 'Configurations',

  ConnectionsData = 'connectionsData',
  CreateAccountNameValidation = 'createAccountNameValidation',
  DecisionLogsQuery = 'decisionLogsQuery',
  Ds0TenantObject = 'tenantAccount',
  Ds0User = 'Ds0User',
  ExecCommandData = 'execCommandData',
  GetPolicyState = 'getPolicyState',
  GetSccProfile = 'sccProfileData',
  GetTenantProfile = 'tenantProfileData',
  Info = 'Info',
  InternalConfig = 'InternalConfig',
  InvitesData = 'invitesData',
  IsConfigurationNameAvailable = 'IsConfigurationNameAvailable',
  IsConnectionNameAvailable = 'IsConnectionNameAvailable',
  IsPolicyNameAvailable = 'isPolicyNameAvailable',
  IsPolicyRepoConnected = 'IsPolicyRepoConnected',
  IsPolicyRepoTagValid = 'IsPolicyRepoTagValid',
  IsRegistryRepoAvailable = 'IsRegistryRepoAvailable',
  ListInstanceRegistrations = 'listInstanceRegistrations',
  ListPolicy = 'listPolicy',
  ListPolicyModules = 'ListPolicyModules',
  ListPolicyRepos = 'policyReposData',
  ListPolicyRepoTags = 'policyRepoTagsData',
  ListRegistryOrgs = 'registryOrganizationsData',
  ListRegistryRepoDigests = 'registryRepoDigestsData',
  ListSccOrganizations = 'sccOrganizationsData',
  ListSccRepo = 'sccRepositoriesData',
  ListSccTemplates = 'sccTemplatesData',
  ListTemplates = 'listTemplates',
  ListTenantMembers = 'ListTenantMembers',
  ManifestReference = 'manifestReference',
  PoliciesData = 'policiesData',
  PolicyBuilderData = 'PolicyBuilderData',
  PolicyData = 'policyData',
  PolicyModuleData = 'policyModuleData',
  ProvidersData = 'providersData',
  RolesData = 'rolesData',
  RunWorkflowData = 'runWorkflowData',
  SetPolicyState = 'setPolicyState',
  SystemStatusData = 'systemStatusData',
  SystemTenantsData = 'systemTenantsData',
  UserData = 'userData',
  UsersData = 'usersData',
}

// default cache interval is 5 minutes
// const REACT_QUERY_STALE_TIME = 5 * 60 * 1000
// connection details contain secrets and are only cached for 20 seconds
// const REACT_QUERY_CONNECTION_STALE_TIME = 20 * 1000

// This allows us to specify just the TError for React Query without specifying the full
// four input types. Infrence can continue to handle the rest!

export const useMutationOptions = <TData = unknown, TVariables = void>(
  mutationHooks: ReactQueryMutationHooks<TData, TVariables>,
  ...queryKeys: (((d: TData, variables: TVariables, context: void) => QueryKey) | QueryKey)[]
): MutationOptions<TData, RpcStatus, TVariables, void> => {
  const queryClient = useQueryClient()
  const showError = useShowError()

  const getQueryKeys = queryKeys.map((queryKey) => {
    if (typeof queryKey === 'function') {
      return queryKey
    } else {
      return () => queryKey
    }
  })

  return {
    ...mutationHooks,
    onError: (error, variables, context) => {
      showError(error)
      mutationHooks.onError?.(error, variables, context)
    },
    onSuccess: (data, variables, context) => {
      getQueryKeys.map((getQueryKey) => {
        queryClient.invalidateQueries(getQueryKey(data, variables, context))
      })
      mutationHooks?.onSuccess?.(data, variables, context)
    },
  }
}

export interface ReactQueryMutationHooks<TData = unknown, TVariables = void> {
  onMutate?: (variables: TVariables) => Promise<void> | void
  onSuccess?: (data: TData, variables: TVariables, context: unknown) => Promise<void> | void
  onError?: (error: RpcStatus, variables: TVariables, context: void) => Promise<void> | void
  onSettled?: (
    data: TData | undefined,
    error: RpcStatus | null,
    variables: TVariables,
    context: void
  ) => Promise<void> | void
}

// default cache interval is 5 minutes
const REACT_QUERY_STALE_TIME = 5 * 60 * 1000
// This allows us to specify just the TError for React Query without specifying the full
// four input types. Infrence can continue to handle the rest!
interface HasRpcStatusErrorHandler {
  onError: (error: RpcStatus) => void
  [x: string]: unknown
}

export const reactQueryDefaultOptions: HasRpcStatusErrorHandler = {
  staleTime: REACT_QUERY_STALE_TIME,
  cacheTime: REACT_QUERY_STALE_TIME,
  onError: () => {},
}
